import React, { useState } from "react";
import axios from "axios";
import config from "../../config";
import Toast from "../Common/Toast";
import { useSelector } from "react-redux";

const formatDateForApi = (dateValue) => {
    if (!dateValue) return "";
    return dateValue;
};

const formatDateForDisplay = (dateValue) => {
    if (!dateValue) return "";
    const parts = String(dateValue).split("-");
    if (parts.length !== 3) return String(dateValue);
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const mapTradeType = (tradeType) => {
    if (tradeType === "B") return "Buy";
    if (tradeType === "S") return "Sell";
    return tradeType || "";
};

const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const ReconciliationReport = ({
    onFix,
    onUpdate,
    title = "Reconciliation Report",
    fetchApiPath = "/eodProcesses/v1/reconciliationReport",
    emptyMessage = "no Missing trade data to show for given dates.",
    getActionLabel,
    showTitle = true,
    showOrderSuccessActions = false
}) => {
    const role = useSelector((state) => state.user.role);
    const isAdminUser = Number(role) === 1;

    const [fromDate, setFromDate] = useState(() => getTodayDate());
    const [toDate, setToDate] = useState(() => getTodayDate());
    const [isLoading, setIsLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [hasFetched, setHasFetched] = useState(false);
    const [error, setError] = useState("");
    const [fixedRowKeys, setFixedRowKeys] = useState({});
    const [fixingRowKeys, setFixingRowKeys] = useState({});
    const [fixFailedRowKeys, setFixFailedRowKeys] = useState({});
    const [updatedRowKeys, setUpdatedRowKeys] = useState({});
    const [updatingRowKeys, setUpdatingRowKeys] = useState({});
    const [toast, setToast] = useState({ show: false, message: "", type: "info" });

    const resolveActionLabel = (row) => {
        if (typeof getActionLabel === "function") {
            return getActionLabel(row);
        }
        return "Fix";
    };

    const getRowKey = (row, index) => `${row?.[12] || row?.[6] || "txn"}-${index}`;

    const hasTransactionId = (row) => {
        const transactionId = Number(row?.[6]);
        return Number.isFinite(transactionId) && transactionId !== 0;
    };

    const fetchReconciliationReport = async () => {
        setError("");
        setRows([]);
        setHasFetched(false);

        if (!fromDate || !toDate) {
            setHasFetched(true);
            setError("Please select both From Date and To Date.");
            return;
        }

        try {
            setIsLoading(true);
            const response = await axios.get(
                config.BL_API_URL + fetchApiPath,
                {
                    params: {
                        fromDate: formatDateForApi(fromDate),
                        toDate: formatDateForApi(toDate)
                    }
                }
            );

            setRows(Array.isArray(response?.data) ? response.data : []);
            setHasFetched(true);
            setFixedRowKeys({});
            setFixingRowKeys({});
            setFixFailedRowKeys({});
            setUpdatedRowKeys({});
            setUpdatingRowKeys({});
            setToast({ show: false, message: "", type: "info" });
        } catch (err) {
            setRows([]);
            setHasFetched(true);
            setError("Unable to load reconciliation report.");
            console.log(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFixClick = async (row, index) => {
        if (!isAdminUser) return;
        if (typeof onFix !== "function") return;

        const rowKey = getRowKey(row, index);
        if (fixedRowKeys[rowKey] || fixingRowKeys[rowKey]) return;

        setFixingRowKeys((prev) => ({ ...prev, [rowKey]: true }));
        const result = await onFix(row);
        const isSuccess = typeof result === "object" ? !!result?.success : !!result;
        const resultMessage =
            typeof result === "object"
                ? result?.message
                : isSuccess
                    ? "Transaction added successfully."
                    : "Failed to add transaction.";
        const isDuplicateIgnored = /duplicate\s+transaction\s+ignored/i.test(String(resultMessage || ""));

        if (isSuccess) {
            setFixedRowKeys((prev) => ({ ...prev, [rowKey]: true }));
            setFixFailedRowKeys((prev) => ({ ...prev, [rowKey]: isDuplicateIgnored }));
            setToast({
                show: true,
                type: "success",
                message: resultMessage || "Transaction added successfully."
            });
        } else {
            setFixFailedRowKeys((prev) => ({ ...prev, [rowKey]: true }));
            setFixedRowKeys((prev) => ({ ...prev, [rowKey]: isDuplicateIgnored }));
            setToast({
                show: true,
                type: isDuplicateIgnored ? "success" : "error",
                message: resultMessage || "Failed to add transaction."
            });
        }

        setFixingRowKeys((prev) => {
            const next = { ...prev };
            delete next[rowKey];
            return next;
        });
    };

    const handleUpdateClick = async (row, index) => {
        if (!isAdminUser) return;
        if (typeof onUpdate !== "function") return;

        const rowKey = getRowKey(row, index);
        if (updatedRowKeys[rowKey] || updatingRowKeys[rowKey]) return;

        setUpdatingRowKeys((prev) => ({ ...prev, [rowKey]: true }));
        const result = await onUpdate(row);
        const isSuccess = typeof result === "object" ? !!result?.success : !!result;
        const resultMessage =
            typeof result === "object"
                ? result?.message
                : isSuccess
                    ? "Order batch status updated successfully."
                    : "Failed to update order batch status.";

        if (isSuccess) {
            setUpdatedRowKeys((prev) => ({ ...prev, [rowKey]: true }));
            setToast({
                show: true,
                type: "success",
                message: resultMessage || "Order batch status updated successfully."
            });
        } else {
            setToast({
                show: true,
                type: "error",
                message: resultMessage || "Failed to update order batch status."
            });
        }

        setUpdatingRowKeys((prev) => {
            const next = { ...prev };
            delete next[rowKey];
            return next;
        });
    };

    return (
        <div className="w-full">
            {showTitle && <h3 className="text-base font-semibold text-gray-800 mb-3">{title}</h3>}
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: "", type: "info" })}
            />
            <div className="flex flex-wrap gap-3 items-end mb-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="border border-gray-300 rounded px-3 py-2"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                    <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="border border-gray-300 rounded px-3 py-2"
                    />
                </div>
                <button
                    type="button"
                    onClick={fetchReconciliationReport}
                    disabled={isLoading}
                    className="bg-gray-700 hover:bg-gray-800 text-white font-bold py-2 px-4 rounded"
                >
                    {isLoading ? "Loading..." : "Execute"}
                </button>
            </div>

            {error && <div className="text-red-600 text-sm mb-3">{error}</div>}

            {hasFetched && rows.length === 0 && !error && (
                <div className="text-gray-700 font-medium">
                    {emptyMessage}
                </div>
            )}

            {rows.length > 0 && (
                <div className="max-h-[60vh] overflow-y-auto overflow-x-auto border border-gray-200 rounded">
                    <table className="min-w-full text-sm text-left">
                        <thead className="sticky top-0 z-10 bg-gray-100 text-gray-700">
                            <tr>
                                <th className="px-3 py-2">Date</th>
                                <th className="px-3 py-2">Batch ID</th>
                                <th className="px-3 py-2">Client Code</th>
                                <th className="px-3 py-2">Scrip Code</th>
                                <th className="px-3 py-2">Transaction ID</th>
                                <th className="px-3 py-2">Trade Type</th>
                                <th className="px-3 py-2">Quantity</th>
                                <th className="px-3 py-2">Unique Number</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2">Message</th>
                                <th className="px-3 py-2">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => {
                                const rowKey = getRowKey(row, index);
                                const isFixed = !!fixedRowKeys[rowKey];
                                const isFixing = !!fixingRowKeys[rowKey];
                                const isFixFailed = !!fixFailedRowKeys[rowKey];
                                const isUpdated = !!updatedRowKeys[rowKey];
                                const isUpdating = !!updatingRowKeys[rowKey];
                                const canFix = hasTransactionId(row);
                                const canUpdate = !canFix || isFixFailed;

                                return (
                                <tr key={rowKey} className="border-t border-gray-100">
                                    <td className="px-3 py-2">{formatDateForDisplay(row?.[0])}</td>
                                    <td className="px-3 py-2">{row?.[3]}</td>
                                    <td className="px-3 py-2">{row?.[4]}</td>
                                    <td className="px-3 py-2">{row?.[5]}</td>
                                    <td className="px-3 py-2">{row?.[6]}</td>
                                    <td className="px-3 py-2">{mapTradeType(row?.[7])}</td>
                                    <td className="px-3 py-2">{row?.[8]}</td>
                                    <td className="px-3 py-2">{row?.[12]}</td>
                                    <td className="px-3 py-2">{row?.[13]}</td>
                                    <td className="px-3 py-2">{row?.[14]}</td>
                                    <td className="px-3 py-2">
                                        {!isAdminUser ? (
                                            <span className="text-gray-400">-</span>
                                        ) : showOrderSuccessActions ? (
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleFixClick(row, index)}
                                                    disabled={!canFix || isFixed || isFixing || isUpdated}
                                                    className={`text-white px-3 py-1 rounded ${!canFix || isFixed || isFixing || isUpdated ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                                                >
                                                    {isFixing ? "Fixing..." : isFixed ? "Fixed" : "Fix"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleUpdateClick(row, index)}
                                                    disabled={!canUpdate || isUpdated || isUpdating}
                                                    className={`text-white px-3 py-1 rounded ${!canUpdate || isUpdated || isUpdating ? "bg-gray-400 cursor-not-allowed" : "bg-slate-600 hover:bg-slate-700"}`}
                                                >
                                                    {isUpdating ? "Updating..." : isUpdated ? "Updated" : "Update"}
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => handleFixClick(row, index)}
                                                disabled={isFixed || isFixing}
                                                className={`text-white px-3 py-1 rounded ${isFixed || isFixing ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                                            >
                                                {isFixing ? "Fixing..." : isFixed ? "Fixed" : resolveActionLabel(row)}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ReconciliationReport;
